<!-- Purpose: Top-bar bell for the BayHive in-app notification inbox. -->
<script>
import Button from 'primevue/button';
import Message from 'primevue/message';
import Popover from 'primevue/popover';
import Skeleton from 'primevue/skeleton';
import { router } from '@inertiajs/vue3';
import { useNotificationCenterStore } from '@/ShopManagement/stores/notificationCenter.js';

export default {
  components: {
    Button,
    Message,
    Popover,
    Skeleton,
  },

  data() {
    return {
      store: useNotificationCenterStore(),
      open: false,
    };
  },

  computed: {
    unreadCount() {
      return this.store.badgeCount;
    },

    // PrimeVue renders no badge for an undefined value, which is what we want
    // at zero rather than a "0" chip.
    badge() {
      if (!this.unreadCount) return undefined;
      return this.unreadCount > 99 ? '99+' : String(this.unreadCount);
    },

    // Both feeds in one time-sorted list.
    items() {
      return this.store.mergedItems;
    },

    initialLoading() {
      return this.store.loading && !this.items.length;
    },

    visibleItems() {
      return this.initialLoading
        ? Array.from({ length: 3 }, (_, index) => ({ key: `loading-${index}` }))
        : this.items;
    },

    triggerLabel() {
      if (!this.unreadCount) return 'Notifications';
      return `Notifications, ${this.unreadCount} unread`;
    },
  },

  created() {
    this.store.hydrateFromProps({
      payload: this.$page.props.bayhiveNotifications,
      userId: this.$page.props.auth?.user?.id,
    });

    // The legacy AutoFlow feed renders in this same bell, so BayHive users see
    // one list. Guarded exactly as Globals/Notifications.vue is.
    this.store.startLegacyBridge({
      wssUrl: this.$page.props.wss_url,
      masterConfig: this.$page.props.master_config,
      user: this.$page.props.auth?.user,
      toggles: this.$page.props.toggles,
    });
  },

  beforeUnmount() {
    this.store.teardownLiveSubscription();
    this.store.teardownLegacyBridge();
  },

  methods: {
    toggle(event) {
      this.$refs.overlay?.toggle(event);
    },

    // The list is only worth fetching once someone actually looks at it; the
    // badge alone comes from the shared Inertia prop and the live channel.
    handleShow() {
      this.open = true;
      this.store.load();
    },

    formatTime(value) {
      if (!value) return '';

      const then = new Date(value);
      const minutes = Math.round((Date.now() - then.getTime()) / 60000);

      if (minutes < 1) return 'Just now';
      if (minutes < 60) return `${minutes} min ago`;
      if (minutes < 60 * 24) return `${Math.round(minutes / 60)} hr ago`;

      return then.toLocaleDateString();
    },

    iconFor(notification) {
      return notification.icon || 'fa-solid fa-bell';
    },

    async openNotification(notification) {
      if (notification.source === 'legacy') {
        await this.store.markLegacyRead(notification);
        this.$refs.overlay?.hide();
        // A full navigation is what refreshes the legacy queue, so keep it.
        if (notification.url) globalThis.window.location.href = notification.url;

        return;
      }

      if (notification.unread) await this.store.markRead(notification.id);

      this.$refs.overlay?.hide();

      if (notification.url) router.visit(notification.url);
    },
  },
};
</script>

<template>
  <span class="bayhive-notification-bell">
    <Button
      type="button"
      icon="fa-solid fa-bell"
      severity="secondary"
      variant="text"
      rounded
      :badge="badge"
      badge-severity="danger"
      aria-haspopup="dialog"
      :aria-expanded="open"
      :aria-label="triggerLabel"
      @click="toggle"
    />

    <Popover
      ref="overlay"
      class="bayhive-notification-bell-popover"
      @show="handleShow"
      @hide="open = false"
    >
      <section class="bayhive-notification-panel" aria-label="Notifications">
        <header class="bayhive-notification-panel-header">
          <strong>Notifications</strong>
          <Button
            v-if="unreadCount"
            type="button"
            label="Mark all read"
            severity="secondary"
            variant="text"
            size="small"
            @click="store.markAllRead()"
          />
        </header>

        <Message v-if="store.error" severity="error" :closable="false">{{ store.error }}</Message>

        <p v-else-if="!initialLoading && !items.length" class="bayhive-notification-panel-muted">
          You have no notifications.
        </p>

        <ul
          v-else
          class="bayhive-notification-list"
          :role="initialLoading ? 'status' : undefined"
          :aria-busy="initialLoading"
          :aria-label="initialLoading ? 'Loading notifications' : undefined"
        >
          <li v-for="notification in visibleItems" :key="notification.key">
            <component
              :is="initialLoading ? 'div' : 'button'"
              :type="initialLoading ? undefined : 'button'"
              :class="[
                'bayhive-notification-item',
                { 'bayhive-notification-item--unread': notification.unread },
              ]"
              @click="!initialLoading && openNotification(notification)"
            >
              <Skeleton v-if="initialLoading" width="1rem" height="1rem" />
              <i v-else :class="iconFor(notification)" aria-hidden="true"></i>
              <span class="bayhive-notification-item-copy">
                <strong><Skeleton v-if="initialLoading" width="80%" height="1em" /><template v-else>{{ notification.title }}</template></strong>
                <small v-if="initialLoading || notification.body"><Skeleton v-if="initialLoading" width="95%" height="1em" /><template v-else>{{ notification.body }}</template></small>
                <small class="bayhive-notification-item-time">
                  <Skeleton v-if="initialLoading" width="5rem" height="1em" />
                  <template v-else>{{ formatTime(notification.createdAt) }}</template>
                </small>
              </span>
              <span
                v-if="notification.unread"
                class="bayhive-notification-item-dot"
                aria-hidden="true"
              ></span>
            </component>
          </li>
        </ul>
      </section>
    </Popover>
  </span>
</template>

<style scoped>
.bayhive-notification-panel {
  display: grid;
  gap: var(--p-bayhive-spacing-sm);
  width: min(24rem, 80vw);
}

.bayhive-notification-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--p-bayhive-spacing-sm);
}

.bayhive-notification-panel-muted {
  margin: 0;
  padding: var(--p-bayhive-spacing-lg) 0;
  color: var(--p-text-muted-color);
  text-align: center;
}

.bayhive-notification-list {
  display: grid;
  max-height: 24rem;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  list-style: none;
}

.bayhive-notification-item {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: start;
  width: 100%;
  gap: var(--p-bayhive-spacing-sm);
  padding: var(--p-bayhive-spacing-sm);
  border: 0;
  border-top: 1px solid var(--p-content-border-color);
  background: transparent;
  color: inherit;
  text-align: left;
}

button.bayhive-notification-item {
  cursor: pointer;
}

button.bayhive-notification-item:hover,
button.bayhive-notification-item:focus-visible {
  background: var(--p-content-hover-background);
}

.bayhive-notification-item i {
  padding-top: 0.15rem;
  color: var(--p-text-muted-color);
}

.bayhive-notification-item-copy {
  display: grid;
  min-width: 0;
  gap: 0.15rem;
}

.bayhive-notification-item-copy small {
  color: var(--p-text-muted-color);
  line-height: 1.4;
}

.bayhive-notification-item-time {
  font-size: 0.75rem;
}

.bayhive-notification-item--unread strong {
  font-weight: 700;
}

.bayhive-notification-item-dot {
  width: 0.5rem;
  height: 0.5rem;
  margin-top: 0.35rem;
  border-radius: 50%;
  background: var(--p-primary-border-color, var(--p-primary-color));
}
</style>
